import React from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

const Home = () => {
  const heroImages = [
    {
      url: 'https://images.unsplash.com/photo-1743229995701-290203f5151a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
      title: 'Designer Anarkali Suits',
      subtitle: 'Exquisite Daily Elegance'
    },
    {
      url: 'https://images.unsplash.com/photo-1756483517695-d0aa21ee1ea1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
      title: 'Palazzo Suit Collection',
      subtitle: 'Bespoke Stitching for Every Occasion'
    },
    {
      url: 'https://images.unsplash.com/photo-1654764745388-978ac6cb8f82?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
      title: 'Straight Cut Elegance',
      subtitle: 'Timeless Sophistication'
    },
    {
      url: 'https://images.unsplash.com/photo-1759840278511-f73a3d62fb9f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
      title: 'Embroidered Kurti Sets',
      subtitle: 'Crafted to Perfection'
    }
  ];

  return (
    <div className="relative" data-testid="home-page">
      {/* Hero Carousel */}
      <div className="h-screen relative" data-testid="hero-carousel">
        <Swiper
          modules={[Autoplay, EffectFade, Pagination]}
          effect="fade"
          autoplay={{
            delay: 4000,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
          }}
          pagination={{
            clickable: true,
            bulletClass: 'swiper-pagination-bullet',
            bulletActiveClass: 'swiper-pagination-bullet-active',
          }}
          loop={true}
          speed={1000}
          className="h-full"
        >
          {heroImages.map((image, index) => (
            <SwiperSlide key={index}>
              <div className="relative h-full">
                <div 
                  className="absolute inset-0 bg-cover bg-top"
                  style={{ backgroundImage: `url(${image.url})`, backgroundPosition: 'center 20%' }}
                >
                  <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                </div>
                
                <div className="relative h-full flex items-end justify-center pb-20 px-4">
                  <div className="max-w-4xl text-center">
                    <h1 
                      className="font-playfair text-5xl sm:text-6xl lg:text-7xl text-[#F8F6F0] mb-4 leading-tight"
                      data-testid={`hero-title-${index}`}
                    >
                      {image.title}
                    </h1>
                    <p className="font-montserrat text-lg sm:text-xl text-[#D4AF37] tracking-widest mb-8">
                      {image.subtitle}
                    </p>
                    <Link to="/collection">
                      <button 
                        className="bg-[#D4AF37] text-[#2B2B2B] font-montserrat px-8 py-3 text-sm tracking-widest hover:bg-[#F8F6F0] transition-colors duration-300"
                        data-testid="explore-collection-button"
                      >
                        EXPLORE COLLECTION
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Featured Section */}
      <section className="py-20 px-4 bg-[#F8F6F0]" data-testid="featured-section">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="font-playfair text-4xl sm:text-5xl text-[#2B2B2B] mb-6">
            Premium Salwar Suits Collection
          </h2>
          <p className="font-montserrat text-base sm:text-lg text-[#2B2B2B] max-w-3xl mx-auto leading-relaxed mb-12">
            Discover our exquisite collection of designer salwar suits, meticulously crafted for the modern 
            woman who values elegance and comfort. From stunning Anarkalis to contemporary palazzo sets, 
            each piece is designed to make you feel confident and beautiful every day.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#2B2B2B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="font-playfair text-xl text-[#2B2B2B] mb-2">Premium Quality</h3>
              <p className="font-montserrat text-sm text-[#2B2B2B]">
                Finest fabrics and intricate craftsmanship
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#2B2B2B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
              <h3 className="font-playfair text-xl text-[#2B2B2B] mb-2">Custom Design</h3>
              <p className="font-montserrat text-sm text-[#2B2B2B]">
                Personalized tailoring to fit your vision
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#2B2B2B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="font-playfair text-xl text-[#2B2B2B] mb-2">Expert Training</h3>
              <p className="font-montserrat text-sm text-[#2B2B2B]">
                Professional courses in tailoring and design
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Collection Preview */}
      <section className="py-20 px-4 bg-white" data-testid="featured-collection-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-4xl sm:text-5xl text-[#2B2B2B] mb-6">
              Featured Collection
            </h2>
            <p className="font-montserrat text-base text-[#2B2B2B] max-w-2xl mx-auto">
              Explore our handpicked selection of the finest ethnic wear pieces
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Collection Item 1 */}
            <div className="group relative overflow-hidden bg-[#F8F6F0] rounded-lg shadow-lg cursor-pointer hover:shadow-2xl transition-shadow duration-300">
              <div className="aspect-[3/4] overflow-hidden relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_attire-fusion/artifacts/38dy0qfa_Gemini_Generated_Image_e4s5pke4s5pke4s5-images-2.jpg"
                  alt="Pink Flutter Gown" 
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4">
                <h3 className="font-playfair text-lg text-[#2B2B2B]">Pink Flutter Gown</h3>
                <p className="font-montserrat text-xs text-[#2B2B2B]">Gowns</p>
              </div>
            </div>

            {/* Collection Item 2 */}
            <div className="group relative overflow-hidden bg-[#F8F6F0] rounded-lg shadow-lg cursor-pointer hover:shadow-2xl transition-shadow duration-300">
              <div className="aspect-[3/4] overflow-hidden relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_attire-fusion/artifacts/suxfm5hm_Gemini_Generated_Image_e4s5pke4s5pke4s5-images-4.jpg"
                  alt="Navy Embroidered Jacket" 
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4">
                <h3 className="font-playfair text-lg text-[#2B2B2B]">Navy Embroidered Jacket</h3>
                <p className="font-montserrat text-xs text-[#2B2B2B]">Jacket Sets</p>
              </div>
            </div>

            {/* Collection Item 3 */}
            <div className="group relative overflow-hidden bg-[#F8F6F0] rounded-lg shadow-lg cursor-pointer hover:shadow-2xl transition-shadow duration-300">
              <div className="aspect-[3/4] overflow-hidden relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_attire-fusion/artifacts/edy4hxf1_Gemini_Generated_Image_e4s5pke4s5pke4s5-images-7.jpg"
                  alt="Mustard Yellow Salwar" 
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4">
                <h3 className="font-playfair text-lg text-[#2B2B2B]">Mustard Yellow Salwar</h3>
                <p className="font-montserrat text-xs text-[#2B2B2B]">Salwar Suits</p>
              </div>
            </div>

            {/* Collection Item 4 - Placeholder */}
            <div className="group relative overflow-hidden bg-[#F8F6F0] rounded-lg shadow-lg cursor-pointer hover:shadow-2xl transition-shadow duration-300">
              <div className="aspect-[3/4] overflow-hidden relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_attire-fusion/artifacts/jp0l1071_Gemini_Generated_Image_e4s5pke4s5pke4s5-images-6.jpg"
                  alt="Designer Collection" 
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4">
                <h3 className="font-playfair text-lg text-[#2B2B2B]">Designer Collection</h3>
                <p className="font-montserrat text-xs text-[#2B2B2B]">Ethnic Wear</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link to="/collection">
              <button className="bg-[#D4AF37] text-[#2B2B2B] font-montserrat px-8 py-3 text-sm tracking-widest hover:bg-[#2B2B2B] hover:text-[#D4AF37] transition-colors duration-300">
                VIEW FULL COLLECTION
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Visit Our Studio Section */}
      <section className="py-20 px-4 bg-white" data-testid="visit-studio-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-4xl sm:text-5xl text-[#2B2B2B] mb-6">
              Visit Our Studio
            </h2>
            <p className="font-montserrat text-base text-[#2B2B2B] max-w-2xl mx-auto">
              Experience our collection in person. Visit our boutique for personalized styling 
              consultations and custom stitching services.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Map */}
            <div className="rounded-lg overflow-hidden shadow-xl h-[400px]" data-testid="studio-map">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3570.6821886709644!2d80.28965!3d26.4850!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399c47e1b88a6ffd%3A0x9f9d6c8c8c9c9c9c!2sKeshav%20Nagar%2C%20Juhi%20Kalan%2C%20Kanpur%2C%20Uttar%20Pradesh%20208014!5e0!3m2!1sen!2sin!4v1641234567890!5m2!1sen!2sin"
                width="100%" 
                height="100%" 
                style={{ border: 0 }}
                allowFullScreen="" 
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Krystal Apparel Store Location"
              ></iframe>
            </div>

            {/* Studio Info */}
            <div className="space-y-6">
              <div>
                <h3 className="font-playfair text-2xl text-[#2B2B2B] mb-4">Krystal Apparel Studio</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                      <svg className="w-6 h-6 text-[#2B2B2B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-montserrat text-base text-[#2B2B2B] font-semibold mb-1">Address</h4>
                      <p className="font-montserrat text-sm text-[#2B2B2B]">
                        Keshav Nagar, Juhi Kalan<br />
                        Saket Nagar, Kanpur<br />
                        Uttar Pradesh 208014, India
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                      <svg className="w-6 h-6 text-[#2B2B2B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-montserrat text-base text-[#2B2B2B] font-semibold mb-1">Store Hours</h4>
                      <p className="font-montserrat text-sm text-[#2B2B2B]">
                        Monday - Saturday: 10:00 AM - 7:00 PM<br />
                        Sunday: By Appointment Only
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                      <svg className="w-6 h-6 text-[#2B2B2B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-montserrat text-base text-[#2B2B2B] font-semibold mb-1">Contact</h4>
                      <p className="font-montserrat text-sm text-[#2B2B2B]">
                        +91 6392782674<br />
                        krystalapparelstore@gmail.com
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <a 
                href="https://share.google/2hdRMhZDkbNHrK6O2" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block w-full"
              >
                <button 
                  className="w-full bg-[#D4AF37] text-[#2B2B2B] font-montserrat py-4 text-sm tracking-widest hover:bg-[#2B2B2B] hover:text-[#D4AF37] transition-colors duration-300"
                  data-testid="get-directions-button"
                >
                  GET DIRECTIONS
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
